import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAgreementContext } from "../../context";
import { useAgreementData } from "../../hooks";
import { useUnsavedChangesWarning } from "@/hooks/useUnsavedChangesWarning";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import {
  createCommentItem,
  removeComment,
  updateComment,
  hasUnsavedComments,
} from "../../utils";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { mapAgreementCommentsToCommentRead } from "../../utils";
import type { CommentRead } from "@/schemas/comments";
import { CommentAddFooter, CommentsList } from "@/modules/comments";
import { mapDraftCommentsToCommentRead } from "../../utils/mapDraftCommentsToCommentRead";
import useAccount from "@/modules/account/hooks/useAccount";

export const AgreementCommentsSection = () => {
  const { t } = useTranslation();
  const { account } = useAccount();
  const { comments, setComments, transitions } = useAgreementContext();
  const [newCommentText, setNewCommentText] = useState("");

  const canComment = transitions.canComment;
  const data = useAgreementData();
  const existingComments = useMemo(() => data.comments ?? [], [data.comments]);

  const hasUnsaved = useMemo(
    () =>
      canComment &&
      (hasUnsavedComments(comments) || newCommentText.trim().length > 0),
    [canComment, comments, newCommentText],
  );

  useUnsavedChangesWarning(hasUnsaved);

  const handleAddCommentField = useCallback(() => {
    const trimmed = newCommentText.trim();
    if (!trimmed) return;
    setComments((prev) => [...prev, createCommentItem(trimmed)]);
    setNewCommentText("");
  }, [newCommentText, setComments]);

  const handleRemoveCommentField = useCallback(
    (id: string) => {
      setComments((prev) => removeComment(prev, id));
    },
    [setComments],
  );

  const handleCommentChange = useCallback(
    (id: string, value: string) => {
      setComments((prev) => updateComment(prev, id, value));
    },
    [setComments],
  );

  const name = "agreementComments";
  const options = useCreateDataTableOptions<CommentRead>(name);

  const savedComments = useMemo(
    () => mapAgreementCommentsToCommentRead(existingComments),
    [existingComments],
  );
  const mergedComments = useMemo(() => {
    if (!canComment) {
      return savedComments;
    }
    return [
      ...savedComments,
      ...mapDraftCommentsToCommentRead(
        comments,
        account?.full_name ?? "",
        account?.system_role_name ?? "",
      ),
    ];
  }, [
    account?.full_name,
    account?.system_role_name,
    canComment,
    comments,
    savedComments,
  ]);

  if (!canComment && existingComments.length === 0) {
    return null;
  }

  const footerHint =
    comments.length === 0
      ? t("agreements.comments.addCommentToSendForCorrection")
      : t("agreements.comments.draftSentInCorrection");
  // Depend only on draft ids (not text changes) to keep popover/table stable while typing.
  const draftCommentIdsKey = useMemo(
    () => comments.map((c) => c.id).join("|"),
    [comments],
  );
  const draftCommentIds = useMemo(
    () => (canComment ? new Set(comments.map((c) => c.id)) : undefined),
    [canComment, draftCommentIdsKey],
  );
  const footer = canComment ? (
    <CommentAddFooter
      value={newCommentText}
      onChange={setNewCommentText}
      onAdd={handleAddCommentField}
      hint={footerHint}
    />
  ) : undefined;

  return (
    <CollapsibleSection title={t("agreements.comments.title")}>
      <CommentsList
        name={name}
        staticComments={mergedComments}
        options={options}
        draftCommentIds={draftCommentIds}
        onRemoveDraft={canComment ? handleRemoveCommentField : undefined}
        onDraftCommentChange={canComment ? handleCommentChange : undefined}
        footer={footer}
      />
    </CollapsibleSection>
  );
};
